/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SocialAuthBody,
  SocialAuthResponse,
  SocialProvider,
} from '../managers/sso';
import {
  useSsoApiService,
  apiKeyQueryOptions,
  handleSuccessfulLogin,
} from './account';

import { describeError } from '../utils/describeError';
export const useSocialLoginWeb = (
  provider: SocialProvider,
  authType: SocialAuthBody['authType'] = 'login',
) => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables?: { restoreAccount?: boolean }) => {
      const clientKey = await queryClient.ensureQueryData(
        apiKeyQueryOptions(api),
      );

      if (!clientKey) throw new Error('Missing API keys');

      const body: SocialAuthBody = {
        authType,
        clientId: clientKey.id || '',
        provider,
        ...(variables?.restoreAccount ? { restoreAccount: true } : {}),
      };

      return api.loginWithSocialWeb(provider, body);
    },
    onSuccess: async (data: SocialAuthResponse) => {
      if (data.refreshToken && data.userId) {
        await handleSuccessfulLogin(queryClient, {
          accessToken: data.accessToken ?? '',
          refreshToken: data.refreshToken,
          userId: data.userId,
        });
      }
    },
    onError: async (error) => {
      console.error('Google login failed', describeError(error));
    },
  });
};
